import {useCallback, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {CircleHelp} from 'lucide-react'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {Sheet, SheetContent, SheetHeader, SheetTitle} from '@/components/ui/sheet'
import {InlineMarkup} from '@/components/shared/InlineMarkup'
import {useI18n} from '@/i18n'
import type {HelpRegistryEntry} from '@/locales/en/help/index'

type CloseCallback = () => void
let activeCloseCallback: CloseCallback | null = null

function registerActive(closeFn: CloseCallback): void {
    if (activeCloseCallback && activeCloseCallback !== closeFn) {
        activeCloseCallback()
    }
    activeCloseCallback = closeFn
}

function unregisterActive(closeFn: CloseCallback): void {
    if (activeCloseCallback === closeFn) {
        activeCloseCallback = null
    }
}

function buildHref(entry: HelpRegistryEntry, docPage: string | undefined): string | null {
    if (!docPage || !docPage.startsWith('/')) return null
    return entry.anchor ? `${docPage}#${entry.anchor}` : docPage
}

interface RegistryHelpIconPortalProps {
    targetElement: Element
    testid: string
    entry: HelpRegistryEntry
    docPage: string | undefined
}

/**
 * Registry-driven help icon — the new (post-265.04) rendering path.
 *
 * Receives a pre-resolved {@link HelpRegistryEntry} from the merged registry
 * and renders the icon + popover without any DOM walk or i18next lookup. The
 * `aria-label` uses {@link HelpRegistryEntry#label} (build-time-derived from
 * the testid). The "More" link is only rendered when `docPage` starts with
 * `/`; an optional `anchor` is appended as a fragment.
 *
 * Activation is click-only (or Enter / Space on keyboard). There is no
 * hover-tooltip preview: see issue 265.14 and the "Hover behaviour" section
 * in `docs/specs/help-system/architecture.md`.
 */
export function RegistryHelpIconPortal({targetElement, testid, entry, docPage}: RegistryHelpIconPortalProps) {
    const i18n = useI18n()
    const [popoverOpen, setPopoverOpen] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [onColouredButton, setOnColouredButton] = useState(false)
    const [container] = useState(() => document.createElement('span'))
    const buttonRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 639px)')
        setIsMobile(mq.matches)
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
        mq.addEventListener('change', handler)
        return () => mq.removeEventListener('change', handler)
    }, [])

    useEffect(() => {
        // eslint-disable-next-line i18next/no-literal-string -- CSS class names, not user-visible text
        container.className = 'help-icon-portal inline-flex items-center'
        // eslint-disable-next-line i18next/no-literal-string -- CSS value, not user-visible text
        container.style.verticalAlign = 'middle'

        // eslint-disable-next-line i18next/no-literal-string -- CSS selector, not user-visible text
        const headingSelector = 'h1, h2, h3, h4, h5, h6, th, button, a[data-testid], [role="heading"], [role="tab"], [data-slot="card-title"], [data-help-anchor="true"]'
        const isHeading = targetElement.matches(headingSelector)
        let headingChild: Element | null = null
        if (!isHeading) {
            const candidates = Array.from(targetElement.querySelectorAll(headingSelector))
            // WHY: explicit [data-help-anchor="true"] children win even when they
            // also carry a data-testid — that's the whole point of the marker
            // (issue 537). For ambiguous heading hosts, fall back to the first
            // child without its own data-testid (avoids stealing another
            // entry's mount point).
            headingChild = candidates.find(c => c.getAttribute('data-help-anchor') === 'true')
                // eslint-disable-next-line i18next/no-literal-string -- DOM attribute name
                ?? candidates.find(c => !c.hasAttribute('data-testid'))
                ?? null
        }
        const isLeaf = targetElement.children.length === 0
        let mountPoint = isHeading ? targetElement : (headingChild ?? (isLeaf ? targetElement : null))
        if (!mountPoint) return

        const mountDisplay = window.getComputedStyle(mountPoint as HTMLElement).display
        if (mountDisplay === 'block' && mountPoint.children.length === 1 && mountPoint.firstElementChild) {
            const inner = mountPoint.firstElementChild as HTMLElement
            const innerDisplay = window.getComputedStyle(inner).display
            if (innerDisplay === 'flex' || innerDisplay === 'inline-flex') {
                mountPoint = inner
            }
        }

        if (mountPoint.tagName === 'TH') {
            // eslint-disable-next-line i18next/no-literal-string -- CSS value, not user-visible text
            (mountPoint as HTMLElement).style.whiteSpace = 'nowrap'
        }

        // WHY: certain host elements can't accept descendants meaningfully —
        // <button> nests buttons (invalid HTML) and propagates disabled
        // pointer-events:none to children, and void elements like <input>,
        // <img>, <select>, <textarea> reject appendChild entirely (icon ends
        // up detached at 0,0). For these hosts, render the icon as the host's
        // next sibling so clicks reach it independently.
        const SIBLING_HOSTS = new Set(['BUTTON', 'INPUT', 'IMG', 'SELECT', 'TEXTAREA'])
        const mountAsSibling = SIBLING_HOSTS.has(mountPoint.tagName) && mountPoint.parentNode != null

        if (mountPoint.tagName === 'BUTTON') {
            const bg = window.getComputedStyle(mountPoint as HTMLElement).backgroundColor
            const isTransparent = !bg || bg.includes('transparent') || /,\s*0\s*\)/.test(bg)
            setOnColouredButton(!isTransparent)
        }

        if (mountAsSibling) {
            mountPoint.parentNode!.insertBefore(container, mountPoint.nextSibling)
        } else {
            mountPoint.appendChild(container)
        }

        const stop = (e: Event) => e.stopPropagation()
        container.addEventListener('click', stop)
        container.addEventListener('pointerdown', stop)
        container.addEventListener('mousedown', stop)

        return () => {
            container.removeEventListener('click', stop)
            container.removeEventListener('pointerdown', stop)
            container.removeEventListener('mousedown', stop)
            container.remove()
        }
    }, [targetElement, container])

    const closePopover = useCallback(() => {
        setPopoverOpen(false)
        unregisterActive(closePopover)
    }, [])

    const handlePopoverChange = useCallback((open: boolean) => {
        if (open) {
            registerActive(closePopover)
            setPopoverOpen(true)
        } else {
            closePopover()
            setTimeout(() => buttonRef.current?.focus(), 0)
        }
    }, [closePopover])

    const href = buildHref(entry, docPage)
    const text = entry.text
    const ariaLabel = entry.label
    // eslint-disable-next-line i18next/no-literal-string -- internal stable testid for E2E tests
    const learnMoreTestId = `help-icon-learn-more-${testid}`

    const popoverContent = (
        <div className="space-y-2">
            <p className="text-sm"><InlineMarkup text={text}/></p>
            {href && (
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary-foreground/80 hover:text-primary-foreground underline"
                    data-testid={learnMoreTestId}
                >
                    {i18n.help.learnMore} →
                </a>
            )}
        </div>
    )

    if (isMobile) {
        return createPortal(
            <>
                <button
                    ref={buttonRef}
                    type="button"
                    aria-label={ariaLabel}
                    onClick={() => handlePopoverChange(true)}
                    className={`ml-1.5 shrink-0 inline-flex items-center cursor-help opacity-60 hover:opacity-100 transition-opacity duration-150 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 rounded-sm animate-in fade-in-0 ${onColouredButton ? 'text-current' : 'text-muted-foreground hover:text-primary'}`}
                    style={{animationDuration: '150ms'}}
                    data-testid="help-icon-button"
                >
                    <CircleHelp className="h-4 w-4"/>
                </button>
                <Sheet open={popoverOpen} onOpenChange={handlePopoverChange}>
                    <SheetContent
                        className="inset-x-0 inset-y-auto bottom-0 top-auto h-auto rounded-t-lg border-t border-l-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom"
                        aria-label={ariaLabel}
                    >
                        <SheetHeader>
                            <SheetTitle className="text-sm font-medium">{ariaLabel}</SheetTitle>
                        </SheetHeader>
                        <div className="mt-2">
                            {popoverContent}
                        </div>
                    </SheetContent>
                </Sheet>
            </>,
            container,
        )
    }

    return createPortal(
        <Popover open={popoverOpen} onOpenChange={handlePopoverChange}>
            <PopoverTrigger asChild>
                <button
                    ref={buttonRef}
                    type="button"
                    aria-label={ariaLabel}
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); handlePopoverChange(!popoverOpen); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    className={`ml-1.5 shrink-0 inline-flex items-center cursor-help opacity-60 hover:opacity-100 transition-opacity duration-150 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 rounded-sm animate-in fade-in-0 ${onColouredButton ? 'text-current' : 'text-muted-foreground hover:text-primary'}`}
                    style={{animationDuration: '150ms'}}
                    data-testid="help-icon-button"
                >
                    <CircleHelp className="h-3.5 w-3.5"/>
                </button>
            </PopoverTrigger>
            <PopoverContent
                side="top"
                align="start"
                sideOffset={8}
                className="w-80 bg-primary text-primary-foreground border-primary"
            >
                {popoverContent}
            </PopoverContent>
        </Popover>,
        container,
    )
}
